import { EventIdentifier, TrackingEventType, useAnalytics, WalletLimits } from '@capsicum/common';
import { forwardRef } from 'react';
import styled from 'styled-components';

import { RoundedCardContainer } from '../../atoms/containers/RoundedCardContainer';
import { Text } from '../../atoms/text/Text';
import { ErrorWithTooltip } from '../../atoms/tooltip/ErrorWithTooltip';
import { CurrencyDisplay, type CurrencyDisplayProps } from '../currencyDisplay/CurrencyDisplay';
import {
	type AmountInputInputRef,
	BaseAmountInput,
	type BaseAmountInputProps
} from '../form/amount-input/BaseAmountInput';

export type AmountSelectionProps = {
	/*
	 * Display a message under the amount range to show specific information to the user.
	 */
	warningMessage?: string;
} & BaseAmountInputProps &
	CurrencyDisplayProps;

export const AmountSelection = forwardRef<AmountInputInputRef, AmountSelectionProps>(function AmountSelection(
	{
		altCurrencyAmount,
		conversionPrice,
		currencyList,
		currencyLogo,
		currencyShortName,
		decimalDetail,
		defaultCurrencyAmount,
		disabled,
		error,
		isConversionRatesLoading,
		max,
		min,
		onChange,
		onClick,
		partnerName,
		showCurrencySelector,
		testID = 'amountSelection',
		tooltipContent,
		value,
		warningMessage
	},
	forwardedRef
) {
	const analytics = useAnalytics();

	const onErrorClick = () => {
		if (error && error.validationType === WalletLimits.OnHold) {
			analytics.sendEvent(TrackingEventType.track, EventIdentifier.MainSellScreenOnHoldErrorOnHoldInfoIconClick);
		}
	};

	const onTooltipClose = () => {
		if (error && error.validationType === WalletLimits.OnHold) {
			analytics.sendEvent(
				TrackingEventType.track,
				EventIdentifier.MainSellScreenOnHoldErrorOnHoldTooltipOutsideClick
			);
		}
	};

	return (
		<S.Wrapper>
			<S.CurrencyAndDisplay>
				<CurrencyDisplay
					altCurrencyAmount={altCurrencyAmount}
					currencyList={currencyList}
					currencyLogo={currencyLogo}
					currencyShortName={currencyShortName}
					defaultCurrencyAmount={defaultCurrencyAmount}
					disabled={disabled}
					isConversionRatesLoading={isConversionRatesLoading}
					partnerName={partnerName}
					showCurrencySelector={showCurrencySelector}
					testID={testID}
					onClick={onClick}
				/>
				<BaseAmountInput
					areConversionRatesLoading={isConversionRatesLoading}
					conversionPrice={conversionPrice}
					decimalDetail={decimalDetail}
					disabled={disabled}
					error={error}
					max={max}
					min={min}
					ref={forwardedRef}
					showErrorMessage={false}
					testID={testID}
					tooltipContent={tooltipContent}
					value={value}
					onChange={onChange}
				/>
			</S.CurrencyAndDisplay>
			<S.Error>
				{error?.message && (
					<ErrorWithTooltip
						errorMessage={error.message}
						errorTooltipContent={error.tooltip}
						testID={testID + 'Error'}
						onClick={onErrorClick}
						onClose={onTooltipClose}
					/>
				)}
				{error?.button && (
					<S.ErrorButton
						$disabled={!error.button.onClick}
						data-testid={testID + '-error-button'}
						variant="text4"
						onClick={error.button.onClick}
					>
						{error.button.label}
					</S.ErrorButton>
				)}
			</S.Error>
			{warningMessage && <S.Warning>{warningMessage}</S.Warning>}
		</S.Wrapper>
	);
});

namespace S {
	export const Wrapper = styled(RoundedCardContainer)`
		flex-direction: column;
		padding: ${({ theme }) => theme.spacings.small};
	`;

	export const CurrencyAndDisplay = styled.div`
		display: flex;
		flex-wrap: wrap;
	`;

	export const Error = styled.div`
		display: flex;
		align-items: center;
		justify-content: flex-end;
		text-align: end;
		flex-wrap: wrap;

		gap: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const ErrorButton = styled(Text)<{ $disabled: boolean }>`
		cursor: ${({ $disabled }) => !$disabled && 'pointer'};
		color: ${({ $disabled, theme }) =>
			$disabled ? theme.colors.borders.error.enabled : theme.colors.text.brand.enabled};
	`;

	export const Warning = styled.div`
		text-align: end;
		${({ theme }) => theme.fonts.text.text5};
		margin-top: ${({ theme }) => theme.spacings.xsmall};
	`;
}
